/**
 * Reusable "show N, +X more" wrapper. Renders a fixed slice of children
 * with a toggle to expand/collapse the rest.
 *
 * Usage:
 *   <ShowMoreGrid items={list} initial={2} testIdPrefix="contest-wins">
 *     {(item, idx) => <Card key={item.id}>{item.title}</Card>}
 *   </ShowMoreGrid>
 */
import { useState } from "react";

const ShowMoreGrid = ({
  items,
  initial = 2,
  testIdPrefix = "showmore",
  gridClassName = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5",
  children,
}) => {
  const [expanded, setExpanded] = useState(false);
  const list = Array.isArray(items) ? items : [];
  const total = list.length;
  const hasOverflow = total > initial;
  const visible = expanded || !hasOverflow ? list : list.slice(0, initial);

  return (
    <div data-testid={`${testIdPrefix}-container`}>
      <div className={gridClassName} data-testid={`${testIdPrefix}-grid`}>
        {visible.map((item, idx) => children(item, idx))}
      </div>
      {hasOverflow && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`${testIdPrefix}-toggle`}
            className="text-xs font-semibold text-amber-200 hover:text-amber-100 bg-black/40 hover:bg-black/60 border border-white/10 rounded-full px-3 py-1.5 transition"
          >
            {expanded ? `Show less` : `+${total - initial} more`}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShowMoreGrid;
